<!-- eslint-disable vue/no-v-html -->
<template>
    <div>
        <PtHeaderDropdown
            v-if="tenantDropdownVisible"
            class="patsnap-biz-tenants"
            :trigger="$attrs.trigger"
        >
            <template #reference>
                <div class="patsnap-biz-tenants__title">
                    <PtHeaderWidget
                        class="patsnap-biz-tenants__widget"
                        :label="activeValue"
                        :post-icon="items.length > 1 ? 'SolidChangeOrder' : ''"
                    />
                </div>
            </template>
            <template #popper>
                <PtList
                    class="patsnap-biz-tenants__popper"
                    :items="items"
                    @itemClick="handleItemClick"
                >
                    <template #default="{item}">
                        <PtTooltip
                            :disabled="!item.disabled"
                            effect="light"
                            placement="left"
                            offset="8"
                            :visible-arrow="false"
                            popper-class="patsnap-biz-tenants__tooltip"
                        >
                            <div
                                slot="content"
                                v-html="getTooltip(item)"
                            />
                            <div class="patsnap-biz-tenants__list-item">
                                <div class="patsnap-ui-list-item__content">
                                    <span>{{ item.label }}</span>
                                    <div class="patsnap-biz-tenants__tags">
                                        <div
                                            v-if="item.domain_restrict"
                                            class="patsnap-biz-tenants__tag"
                                        >
                                            {{ $t('PtTenants.domainLimit') }}
                                        </div>
                                        <div
                                            v-if="item.ip_restrict"
                                            class="patsnap-biz-tenants__tag"
                                        >
                                            {{ $t('PtTenants.IPLimit') }}
                                        </div>
                                        <div
                                            v-if="item.sso_restrict"
                                            class="patsnap-biz-tenants__tag"
                                        >
                                            {{ $t('PtTenants.ssoLimit') }}
                                        </div>
                                    </div>
                                </div>

                                <PtIcon
                                    v-if="item.postIcon"
                                    :icon="item.postIcon"
                                    class="patsnap-ui-list-item__post-icon"
                                />
                            </div>
                        </PtTooltip>
                    </template>
                </PtList>
            </template>
        </PtHeaderDropdown>
        <div
            v-if="tenantTitleVisible"
            class="patsnap-biz-tenants__title"
        >
            <div class="patsnap-biz-tenants__title-content">
                {{ activeValue }}
            </div>
        </div>

        <PtDialog
            v-if="service.switchTenantDialogVisible"
            :title="$t('PtTenants.switchTenantTitle')"
            :visible.sync="service.switchTenantDialogVisible"
            :append-to-body="true"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            width="400px"
            class="patsnap-biz-tenants__switch-dialog"
        >
            <div>
                {{ $t('PtTenants.youWillSwitchedTo', {tenantName: selectedCurrentTenant.label}) }}<br>
                {{ $t('PtTenants.switchTenantTip') }}
            </div>
            <span
                slot="footer"
                class="dialog-footer"
            >
                <button
                    class="pt-ui-btn"
                    data-size="medium"
                    :disabled="loading"
                    @click="service.switchTenantDialogVisible = false"
                >
                    <span>{{ $t('PtTenants.cancel') }}</span>
                </button>
                <button
                    class="pt-ui-btn patsnap-biz-tenants__switch-dialog__confirm-btn"
                    data-type="primary"
                    data-size="medium"
                    :disabled="loading"
                    @click="handleOk"
                >
                    <span
                        :style="{'opacity': loading ? 0 : 1}"
                    >{{ $t('PtTenants.ok') }}</span>
                    <PtIcon
                        v-if="loading"
                        icon="SolidLoadingBars"
                    />
                </button>
            </span>
        </PtDialog>
    </div>
</template>

<script>
import PtList from '@patsnap-biz/list';
import PtIcon from '@patsnap-biz/icon';
import PtDialog from '@patsnap-biz/dialog';
import Toast from '@patsnap-biz/toast';
import Tooltip from 'element-ui/lib/tooltip';
import _ from 'lodash';
import PtHeaderDropdown from './HeaderDropdown';
import PtHeaderWidget from './HeaderWidget';
import cn from '../locales/tenants/cn';
import en from '../locales/tenants/en';
import jp from '../locales/tenants/jp';
import tw from '../locales/tenants/tw';
import '@patsnap-ui/icon/assets/solid/ChangeOrder.svg';
import '@patsnap-ui/icon/assets/solid/tick.svg';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';

export default {
    name: 'PtTenants',
    components: {
        PtList,
        PtHeaderDropdown,
        PtHeaderWidget,
        PtIcon,
        PtTooltip: Tooltip,
        PtDialog,
    },
    inject: ['service'],
    i18n: {
        fallbackLocale: 'cn',
        messages: {
            cn, en, jp, tw,
        },
    },
    props: {
        value: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    data() {
        return {
            loading: false,
        };
    },
    computed: {
        selectedCurrentTenant() {
            return this.service.currentSwitchTenantInfo;
        },
        items() {
            return this.value.map((item) => ({
                ...item,
                label: item?.label || this.$t('PtTenants.personalTenant'),
                postIcon: 'SolidTick',
            }));
        },
        activeValue() {
            const activeItem = this.items.find((item) => !!item.active);
            return activeItem?.label;
        },
        tenantDropdownVisible() {
            return this.items.length > 1 && this.service.canSwitchTenant;
        },
        tenantTitleVisible() {
            return !this.tenantDropdownVisible && !!this.items[0]?.tenant_id;
        },
    },
    methods: {
        async handleItemClick(item) {
            if (item.active) return;
            if (item.disabled) return;
            const beforeSwitchTenantHandlers = (_.get(this.service, 'userCenterService.beforeSwitchTenantHandlers')) || undefined;
            if (beforeSwitchTenantHandlers) {
                await Promise.all(Array.from(beforeSwitchTenantHandlers).map((h) => h(item)));
            }
            this.service.currentSwitchTenantInfo = item;
            this.service.switchTenantDialogVisible = true;
        },

        handleOk() {
            this.loading = true;
            this.service.switchTenants(this.selectedCurrentTenant.tenant_id).then(() => {
                this.service.switchTenantDialogVisible = false;
            }).catch((e) => {
                const errorCode = _.get(e, 'response.data.numeric_error_code');
                if (this.service.isMfaError(errorCode)) {
                    this.service.goMfaRequiredPage(errorCode, this.selectedCurrentTenant.tenant_id);
                    return;
                }
                const hasI18n = `PtTenants.error${errorCode}` !== this.$t(`PtTenants.error${errorCode}`);
                Toast({
                    type: 'error',
                    message: hasI18n ? this.$t(`PtTenants.error${errorCode}`) : this.$t('PtTenants.switchTenantFailed'),
                    duration: 3000,
                });
            }).finally(() => {
                this.loading = false;
            });
        },

        getTooltip(item) {
            const msgAry = [];
            if (!item.account_status_active) {
                msgAry.push(this.$t('PtTenants.accountStatusNotActiveTip'));
            }
            if (item.domain_restrict) {
                msgAry.push(this.$t('PtTenants.domainLimitTip'));
            }
            if (item.ip_restrict) {
                msgAry.push(this.$t('PtTenants.IPLimitTip'));
            }
            if (item.sso_restrict) {
                if (item.sso_restrict_type === 'DINGTLK') {
                    msgAry.push(this.$t('PtTenants.dingdingTip'));
                } else if (item.sso_restrict_type === 'FEISHU') {
                    msgAry.push(this.$t('PtTenants.feishuTip'));
                } else if (item.sso_restrict_type === 'WECHAT_ENTERPRISE') {
                    msgAry.push(this.$t('PtTenants.wecomTip'));
                } else if (item.sso_restrict_type === 'JWT') {
                    msgAry.push(this.$t('PtTenants.jwtTip'));
                } else {
                    msgAry.push(this.$t('PtTenants.ssoTip'));
                }
            }
            return msgAry.join('<div style="margin-bottom: 8px"></div>');
        },
    },
};
</script>
